import SEO from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';

export default function Privacy() {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS content takes priority, then i18n, then hardcoded fallback)
  const get = (key: string, fallback: string) =>
    content?.getContent('privacy', key, t(`privacy.${key}`, fallback)) ?? t(`privacy.${key}`, fallback);

  // Helper to render list items from pipe-separated string
  const renderList = (key: string, fallback: string) => {
    const items = get(key, fallback).split('|');
    return (
      <ul className="list-disc list-inside text-gray-600 space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <SEO page="privacy" />
      <div className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <EditableElement section="privacy" field="title">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              {get('title', 'Privacy Policy')}
            </h1>
          </EditableElement>

          <div className="prose prose-gray max-w-none">
            <EditableElement section="privacy" field="last_updated">
              <p className="text-gray-600 mb-6">
                {get('last_updated', 'Last updated: December 2024')}
              </p>
            </EditableElement>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_1_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_1_title', '1. Introduction')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_1_content">
                <p className="text-gray-600">
                  {get('section_1_content', 'RUMIO ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application, browser extension, and website (collectively, the "Service"). By using the Service, you agree to the collection and use of information in accordance with this policy.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_2_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_2_title', '2. Information We Collect')}
                </h2>
              </EditableElement>

              <EditableElement section="privacy" field="section_2_subtitle_1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {get('section_2_subtitle_1', '2.1 Information You Provide')}
                </h3>
              </EditableElement>
              <div className="mb-4">
                {renderList('section_2_list_1', 'Account information (email address, password)|Profile information you choose to provide|Products you add to your watchlist|Communication preferences')}
              </div>

              <EditableElement section="privacy" field="section_2_subtitle_2">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {get('section_2_subtitle_2', '2.2 Information Collected Automatically')}
                </h3>
              </EditableElement>
              <div className="mb-4">
                {renderList('section_2_list_2', 'Device information (device type, operating system)|Usage data (features used, pages visited)|Location data (country/region for store selection)|Log data (IP address, browser type, timestamps)')}
              </div>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_3_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_3_title', '3. How We Use Your Information')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_3_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_3_intro', 'We use the information we collect to:')}
                </p>
              </EditableElement>
              {renderList('section_3_list', 'Provide and maintain the Service|Send you notifications about product availability and price changes|Process your subscription payments|Improve and personalize your experience|Communicate with you about updates and support|Detect and prevent fraud or abuse|Comply with legal obligations')}
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_4_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_4_title', '4. Data Sharing')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_4_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_4_intro', 'We do not sell your personal information. We may share your information with:')}
                </p>
              </EditableElement>
              {renderList('section_4_list', 'Service providers who assist in operating our Service (payment processors, hosting providers)|Analytics providers to help us understand usage patterns|Law enforcement when required by law')}
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_5_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_5_title', '5. Data Security')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_5_content">
                <p className="text-gray-600">
                  {get('section_5_content', 'We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_6_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_6_title', '6. Your Rights')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_6_intro">
                <p className="text-gray-600 mb-4">
                  {get('section_6_intro', 'Depending on your location, you may have the right to:')}
                </p>
              </EditableElement>
              {renderList('section_6_list', 'Access your personal data|Correct inaccurate data|Delete your data|Object to data processing|Export your data|Withdraw consent')}
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_7_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_7_title', '7. Data Retention')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_7_content">
                <p className="text-gray-600">
                  {get('section_7_content', 'We retain your personal information for as long as your account is active or as needed to provide you with our Service. You can delete your account at any time through the Settings page, which will remove your personal data.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_8_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_8_title', '8. Children\'s Privacy')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_8_content">
                <p className="text-gray-600">
                  {get('section_8_content', 'Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_9_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_9_title', '9. Changes to This Policy')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_9_content">
                <p className="text-gray-600">
                  {get('section_9_content', 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.')}
                </p>
              </EditableElement>
            </section>

            <section className="mb-8">
              <EditableElement section="privacy" field="section_10_title">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {get('section_10_title', '10. Contact Us')}
                </h2>
              </EditableElement>
              <EditableElement section="privacy" field="section_10_content">
                <p className="text-gray-600">
                  {get('section_10_content', 'If you have any questions about this Privacy Policy, please contact us through our Support page.')}
                </p>
              </EditableElement>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
